import {
  mapFromFolderNamePreview,
  mapFromOutputLocationBindings,
  mapToFolderNamePreviewRequest,
  mapToOutputLocationDefaultBindingPut,
  mapToOutputLocationTemplateBindingPut,
} from 'mappings/outputLocationBindingMapping';
import { DocumentOutputFolderRule, DocumentOutputPurpose } from 'models/documentOutputLocation';
import { FolderNamePreviewDTO, OutputLocationBindingsDTO } from 'models/dto/outputLocationBindingDTO';
import { FolderNamePreview, OutputLocationBindings } from 'models/outputLocationBinding';
import AppError from 'utils/appError';
import http from './httpService';

export async function apiGetBindings(
  purpose: DocumentOutputPurpose,
  accessToken: string,
): Promise<OutputLocationBindings> {
  try {
    const ar = await http.get<OutputLocationBindingsDTO>(
      `/settings/outputlocations/${purpose}/bindings`,
      http.getRequestConfig(accessToken),
    );

    return mapFromOutputLocationBindings(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** The tenant row: the folder rule and the folder name format of every task without an override. */
export async function apiSetDefaultBinding(
  purpose: DocumentOutputPurpose,
  folderRule: DocumentOutputFolderRule | undefined,
  folderNameFormat: string | undefined,
  accessToken: string,
): Promise<void> {
  try {
    await http.put(
      `/settings/outputlocations/${purpose}/bindings/default`,
      mapToOutputLocationDefaultBindingPut(folderRule, folderNameFormat),
      http.getRequestConfig(accessToken),
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * A template row; an undefined value takes the tenant row's, and a row with no value left is removed. The folder lies
 * in the row's own category or, without one, in the tenant's; no folderDriveItemId is that category's root.
 */
export async function apiSetTemplateBinding(
  purpose: DocumentOutputPurpose,
  templateTaskId: number,
  listId: number | undefined,
  folderDriveItemId: string | undefined,
  folderName: string | undefined,
  folderRule: DocumentOutputFolderRule | undefined,
  folderNameFormat: string | undefined,
  accessToken: string,
): Promise<void> {
  try {
    await http.put(
      `/settings/outputlocations/${purpose}/bindings/templates/${templateTaskId}`,
      mapToOutputLocationTemplateBindingPut(listId, folderDriveItemId, folderName, folderRule, folderNameFormat),
      http.getRequestConfig(accessToken),
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveTemplateBinding(
  purpose: DocumentOutputPurpose,
  templateTaskId: number,
  accessToken: string,
): Promise<void> {
  try {
    await http.delete(
      `/settings/outputlocations/${purpose}/bindings/templates/${templateTaskId}`,
      http.getRequestConfig(accessToken),
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** The format rendered on a real task when sampleTaskId is given, otherwise on the catalogue's sample values. */
export async function apiPreviewFolderName(
  purpose: DocumentOutputPurpose,
  format: string,
  sampleTaskId: number | undefined,
  accessToken: string,
): Promise<FolderNamePreview> {
  try {
    const ar = await http.post<FolderNamePreviewDTO>(
      `/settings/outputlocations/${purpose}/bindings/preview`,
      mapToFolderNamePreviewRequest(format, sampleTaskId),
      http.getRequestConfig(accessToken),
    );

    return mapFromFolderNamePreview(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
